import './Shop.css'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import ProductCard, { pricing } from '../components/ProductCard'
import { useCart } from '../context/CartContext'
import { byOrder, useBrandMap, useCategories, useDevices, useProducts } from '../hooks/useSiteData'

export default function Shop() {
  const [sp, setSp] = useSearchParams()
  const { wish } = useCart()
  const products = useProducts()
  const categories = useCategories()
  const devices = useDevices() // brands and models that have products
  const brandMap = useBrandMap() // every brand with its models (defaults + added in the admin)
  const catNames = [...categories].sort(byOrder).map(c => c.name)
  const category = sp.get('category') || 'All'
  const brand = sp.get('brand') || ''
  const model = sp.get('model') || ''
  const q = sp.get('q') || ''
  const onlyWish = sp.get('wish') === '1'
  const sort = sp.get('sort') || ''

  const upd = (k, v) => {
    const n = new URLSearchParams(sp)
    v ? n.set(k, v) : n.delete(k)
    if (k === 'brand') n.delete('model')
    setSp(n)
  }

  const list = useMemo(() => {
    const brandModels = brand ? brandMap[brand] || [] : []
    // a product fits a brand if it is saved for that brand, or has no brand and no models (fits any phone)
    const fitsBrand = p => !brand || (p.brand ? p.brand === brand : !p.models || p.models.some(m => brandModels.includes(m)))
    // no models listed = every model of its brand (or every phone when it has no brand)
    const fitsModel = p => !model || (p.models ? p.models.includes(model) : !p.brand || !!brandMap[p.brand]?.includes(model))
    let r = products.filter(p =>
      (category === 'All' || p.category === category) && fitsBrand(p) && fitsModel(p) &&
      p.name.toLowerCase().includes(q.toLowerCase()) &&
      (!onlyWish || wish.includes(p.id)))
    // sort by the price customers actually pay (sale price when there is one)
    if (sort === 'low') r = [...r].sort((a, b) => pricing(a).price - pricing(b).price)
    if (sort === 'high') r = [...r].sort((a, b) => pricing(b).price - pricing(a).price)
    return r
  }, [products, brandMap, category, brand, model, q, onlyWish, sort, wish])

  const title = onlyWish ? 'Your wishlist' : model || (brand ? `${brand} phones` : category === 'All' ? 'All products' : category)

  return (
    <div className="shop">
      <aside className="shop__filters" aria-label="Filters">
        <label>Brand
          <select value={brand} onChange={e => upd('brand', e.target.value)}>
            <option value="">All brands</option>
            {Object.keys(devices).map(b => <option key={b}>{b}</option>)}
          </select>
        </label>
        <label>Model
          <select value={model} onChange={e => upd('model', e.target.value)}>
            <option value="">All models</option>
            {(brand ? devices[brand] || [] : Object.values(devices).flat()).map(m => <option key={m}>{m}</option>)}
          </select>
        </label>
        <label>Sort by
          <select value={sort} onChange={e => upd('sort', e.target.value)}>
            <option value="">Featured</option>
            <option value="low">Price: low to high</option>
            <option value="high">Price: high to low</option>
          </select>
        </label>
        {catNames.length > 0 && (
          <div className="chips">
            {['All', ...catNames].map(c => (
              <button key={c} aria-pressed={category === c} className={`chip ${category === c ? 'chip--on' : ''}`} onClick={() => upd('category', c === 'All' ? '' : c)}>{c}</button>
            ))}
          </div>
        )}
      </aside>
      <section className="shop__main">
        <div className="sec-head">
          <div><h1 className="section__title">{title}</h1><p>{list.length} products{q && ` for "${q}"`}</p></div>
          {(brand || model || q || category !== 'All' || onlyWish) && <button className="link shop__clear" onClick={() => setSp({})}>Clear filters</button>}
        </div>
        {list.length === 0
          ? <p className="empty">{onlyWish ? 'Your wishlist is empty. Tap the heart on any product to save it.' : products.length ? 'No products match. Try clearing a filter.' : 'Products are coming soon.'}</p>
          : <div className="shop__grid">{list.map(p => <ProductCard key={p.id} p={p} />)}</div>}
      </section>
    </div>
  )
}